import { Component, OnInit } from '@angular/core';
import { AuthStoreService, ContentStoreService, SiteLanguageStoreService } from '@motivforce/mx-library-angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter } from 'rxjs';
import { AcceleratorProgress } from 'src/app/core/model/lenovo/accelerator-progress';
import { DownloadProgramGuideContent } from 'src/app/core/model/lenovo/download-program-guide-content';
import { SalesTargetOption } from 'src/app/core/model/lenovo/sales-target-option';

import { Currency } from '../../../core/model/core/currency';
import { RebateSummary } from '../../../core/model/lenovo/rebate-summary';
import { UserStoreService } from '../../../core/store/user-store.service';

@UntilDestroy()
@Component({
  selector: 'app-accelerator-widget',
  templateUrl: './accelerator-widget.component.html',
  styleUrls: ['./accelerator-widget.component.scss'],
})
export class AcceleratorWidgetComponent implements OnInit {
  idgProgress?: AcceleratorProgress;
  isgProgress?: AcceleratorProgress;
  showComponent = false;
  currency?: Currency;
  lastBand: SalesTargetOption;
  bonusRebate: number;

  isIdgLastBandAchived = false;
  isIsgLastBandAchived = false;

  isoCodeProgramGuideUrlMap: any;
  programGuideUrl: string;
  downloadProgramGuideContent: DownloadProgramGuideContent;

  constructor(
    private userStore: UserStoreService,
    private contentStore: ContentStoreService,
    private siteLanguageStore: SiteLanguageStoreService,
    private translateService: TranslateService,
    private authStore: AuthStoreService
  ) {}

  ngOnInit() {
    this.userStore.currency$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((currency) => {
      this.currency = currency;
      this.setProgramGuideUrl();
    });

    this.userStore.rebateSummaryDetails$
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe((rebateSummaries: RebateSummary[]) => {
        const rebateSummary = rebateSummaries.find((rs) => rs.isCurrentQ === true);

        if (rebateSummary && rebateSummary.businessGroup === 'ISG+IDG') {
          this.lastBand = rebateSummary.rebateBands[rebateSummary.rebateBands.length - 1];
          this.bonusRebate = rebateSummary.bonusRebateDueLocalCurrency;
          this.showComponent = true;
          let index = rebateSummary.rebateBands.findIndex(
            (b) =>
              rebateSummary.qtrRevIDGLocalCurrency < b.salesTarget ||
              rebateSummary.qtrRevISGLocalCurrency < b.salesTarget
          );
          if (index === -1) index = rebateSummary.rebateBands.length - 1;
          const currentBand = rebateSummary.rebateBands[index];

          this.idgProgress = new AcceleratorProgress(
            currentBand.salesTarget,
            rebateSummary.qtrRevIDGLocalCurrency,
            currentBand.band
          );
          this.isgProgress = new AcceleratorProgress(
            currentBand.salesTarget,
            rebateSummary.qtrRevISGLocalCurrency,
            currentBand.band
          );

          if (this.idgProgress.value >= this.idgProgress.max) {
            this.isIdgLastBandAchived = true;
          }
          if (this.isgProgress.value >= this.isgProgress.max) {
            this.isIsgLastBandAchived = true;
          }
        }
      });

    this.contentStore.currentContent$
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        filter(
          (content) =>
            content.id === this.downloadProgramGuideContent?.isgProgramGuideUrlListId ||
            content.id === this.downloadProgramGuideContent?.idgProgramGuideUrlListId ||
            content.id === this.downloadProgramGuideContent?.idgIsgProgramGuideUrlListId
        )
      )
      .subscribe((content) => {
        if (content.children) {
          this.isoCodeProgramGuideUrlMap = {};

          content.children.forEach((item) => {
            const itemField = item.fields.find((f) => f.name === 'URL');
            this.isoCodeProgramGuideUrlMap[item.name] = itemField!.value.value;
          });
        }

        this.setProgramGuideUrl();
      });

    this.userStore.downloadProgramGuideContent$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((content) => {
      this.downloadProgramGuideContent = content;
    });

    combineLatest([this.authStore.userSettings$.pipe(filter(Boolean)), this.siteLanguageStore.language$])
      .pipe(untilDestroyed(this))
      .subscribe(([settings, siteLanguage]) => {
        const language = siteLanguage || settings.language || 'en-GB';

        const canImpersonate = settings.claims.includes('user.impersonate');
        if (!canImpersonate) {
          this.userStore.getDownloadProgramGuideContent(language);
        }
      });
  }

  calculateRemainingProgress(businessGroupProgress: AcceleratorProgress | undefined): string {
    const target = businessGroupProgress?.max ?? 0;
    const current = businessGroupProgress?.value ?? 0;
    const remaining = target - current;
    return remaining > 0 ? remaining.toFixed(2) : '0';
  }

  private setProgramGuideUrl(): void {
    if (this.isoCodeProgramGuideUrlMap && this.currency) {
      const programGuideUrlLink = this.isoCodeProgramGuideUrlMap[this.currency.isoCode];
      const programGuideLinkText = this.translateService.instant('Program guide');
      this.programGuideUrl = `<a
      href="${programGuideUrlLink}"
      target="_blank"
      class="program-guide-link"
    >${programGuideLinkText}</a>`;
    }
  }
}
